import { Skeleton } from '@mui/material';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styles from './index.module.scss';

export const LearningArticleCard: React.FunctionComponent<LearningArticleCardProps> = ({
  loading,
  data,
  sectionType,
  navigate,
  tagDetails,
  navigateTo,
  isHidden,
}) => {
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const navigationType = urlParams.get('type');
  const catId = urlParams.get('categoryId');
  const catName = urlParams.get('categoryName');
  const [imgLoading, setImgLoading] = useState(true);
  const allBlogPostsId = -999;
  const skipOnboarding = urlParams.get('skipOnboarding');
  const validatedSkipOnboarding = Boolean(skipOnboarding === 'true');

  const handleImageLoad = () => {
    setImgLoading(false);
  };

  const navigation = (id: number, categoryName: string) => {
    if (sectionType === 'categories') {
      if (navigationType === 'learningHome') {
        urlParams.set('type', 'singleCategoryContent');
      } else {
        urlParams.set('type', 'singleCategoryContentTags');
      }

      if (urlParams.has('categoryId')) {
        urlParams.set('categoryId', id.toString());
      } else {
        urlParams.append('categoryId', id.toString());
      }

      if (urlParams.has('categoryName')) {
        urlParams.set('categoryName', categoryName);
      } else {
        urlParams.append('categoryName', categoryName);
      }

      history.push({ search: urlParams.toString() });
    }
  };

  const handleCardClick = () => {
    if (sectionType === 'categories' && navigate) {
      navigation(data?.id || 0, data?.title || '');
    } else if (sectionType === 'posts' || sectionType === 'categoryPosts') {
      if (tagDetails && tagDetails.tagId && tagDetails.tagId !== -1) {
        history.push(
          validatedSkipOnboarding
            ? `/post/${data?.id}?categoryId=${tagDetails.tagId}&categoryName=${tagDetails.title}&skipOnboarding=true`
            : `/post/${data?.id}?categoryId=${tagDetails.tagId}&categoryName=${tagDetails.title} `,
        );
      } else if (navigationType === 'singleCategoryContentTags' && catId && catName) {
        history.push(
          validatedSkipOnboarding
            ? `/post/${data?.id}?categoryId=${catId}&categoryName=${catName}&skipOnboarding=true`
            : `/post/${data?.id}?categoryId=${catId}&categoryName=${catName} `,
        );
      } else {
        history.push(
          validatedSkipOnboarding ? `/post/${data?.id}?skipOnboarding=true` : `/post/${data?.id}`,
        );
      }
    } else {
      history.push(
        validatedSkipOnboarding
          ? `/posts?type=learningHome&skipOnboarding=true`
          : `/posts?type=learningHome`,
      );
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  if (data) {
    const safeTitle = data.title.replace(/<[^>]*>?/gm, '');

    return (
      <div
        className={styles.learningItem}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={isHidden ? -1 : 0}
        aria-label={`${safeTitle}. ${
          sectionType === 'categories' ? 'View category' : 'Read article'
        }`}
        aria-hidden={isHidden}
      >
        <div className={styles.learningItemInner}>
          <div
            className={styles.learningItemInnerImage}
            aria-hidden={imgLoading}
            tabIndex={imgLoading ? -1 : undefined}
          >
            {imgLoading && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="100%"
                className={styles.image}
                aria-hidden="true"
                tabIndex={-1}
              />
            )}
            <img
              src={data.innerImageUrl}
              alt={tagDetails?.title || ''}
              className={styles.image}
              onLoad={handleImageLoad}
              style={{ display: imgLoading ? 'none' : 'block' }}
              aria-hidden="true"
              tabIndex={-1}
            />
          </div>
          <div className={styles.learningItemInnerContent}>
            <div dangerouslySetInnerHTML={{ __html: data.title }} />
          </div>
        </div>
      </div>
    );
  }

  return null;
};

LearningArticleCard.defaultProps = {
  loading: false,
  data: null,
  navigate: () => null,
  tagDetails: { tagId: -1, title: '' },
  navigateTo: 'oldPosts',
  isHidden: false,
};

interface LearningArticleCardProps
  extends React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> {
  loading?: boolean;
  data?: { id: number; innerImageUrl: string; title: string } | null;
  sectionType: string;
  navigate?: (id: number, title: string) => void;
  tagDetails?: { tagId: number | undefined; title: string | undefined };
  navigateTo?: string;
  isHidden?: boolean;
}
